import { useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Gauge,
  Inbox,
  Minus,
  ShieldCheck,
  Smile,
  Timer,
} from 'lucide-react';
import { useAnalyticsOverview, type AnalyticsDays } from '@/api/analytics';
import { useTeams } from '@/api/teams';
import type { AnalyticsTotals } from '@/api/types';
import { Avatar, Card, ErrorState, PageHeader, Segmented, Skeleton } from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatDuration, formatNumber, humanize } from '@/lib/format';
import { CHANNEL_LABEL } from '../tickets/meta';
import type { TicketChannel } from '@/api/types';
import { BarList, Legend, LineChart, SERIES } from './charts';
import { Kpi } from './Kpi';

const RANGES: AnalyticsDays[] = [7, 14, 30, 90];

type Better = 'up' | 'down';

function Delta({
  cur,
  prev,
  better,
  unit = '%',
}: {
  cur: number | null;
  prev: number | null;
  better: Better;
  unit?: '%' | 'pts';
}) {
  if (cur == null || prev == null || prev === 0)
    return <span className="text-xs text-slate-400">No prior data</span>;
  const change = unit === 'pts' ? cur - prev : ((cur - prev) / prev) * 100;
  const rounded = Math.round(change * 10) / 10;
  if (Math.abs(rounded) < 0.5) {
    return (
      <span className="flex items-center gap-1 text-xs text-slate-500">
        <Minus className="h-3.5 w-3.5" aria-hidden /> Flat vs previous
      </span>
    );
  }
  const good = rounded > 0 === (better === 'up');
  const Icon = rounded > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-xs font-medium',
        good ? 'text-emerald-700' : 'text-red-700',
      )}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {rounded > 0 ? '+' : ''}
      {rounded}
      {unit === 'pts' ? ' pts' : '%'} <span className="font-normal text-slate-500">vs previous</span>
    </span>
  );
}

function AnalyticsSkeleton() {
  return (
    <div role="status" aria-label="Loading analytics" className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-36 rounded-2xl" />
        ))}
      </div>
      <div className="grid gap-5 xl:grid-cols-3">
        <Skeleton className="h-80 rounded-2xl xl:col-span-2" />
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    </div>
  );
}

const pct = (v: number | null) => (v == null ? '—' : `${v}%`);

export function AnalyticsPage() {
  const [days, setDays] = useState<AnalyticsDays>(30);
  const [asTable, setAsTable] = useState(false);
  const overview = useAnalyticsOverview(days);
  const teams = useTeams();
  const teamName = (id: string | null) =>
    id ? (teams.data?.find((t) => t.id === id)?.name ?? 'Unknown team') : 'No team';

  const rangePicker = (
    <Segmented
      label="Date range"
      value={days}
      onChange={setDays}
      options={RANGES.map((d) => ({ value: d, label: `${d} days` }))}
    />
  );

  if (overview.isPending) {
    return (
      <>
        <PageHeader eyebrow="Insights" title="Analytics" actions={rangePicker} />
        <AnalyticsSkeleton />
      </>
    );
  }
  if (overview.error) {
    return (
      <>
        <PageHeader eyebrow="Insights" title="Analytics" actions={rangePicker} />
        <Card>
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        </Card>
      </>
    );
  }

  const o = overview.data;
  const c: AnalyticsTotals = o.current;
  const p: AnalyticsTotals = o.previous;

  return (
    <div className={cn('transition-opacity', overview.isPlaceholderData && 'opacity-60')}>
      <PageHeader
        eyebrow="Insights"
        title="Analytics"
        description={`Last ${days} days (IST), compared with the ${days} days before.`}
        actions={rangePicker}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <Kpi
          label="Tickets created"
          value={formatNumber(c.created)}
          icon={Inbox}
          tint="bg-sky-50 text-sky-600 ring-sky-100"
          trend={{ values: o.volume.map((v) => v.created), color: SERIES[0] }}
        >
          <Delta cur={c.created} prev={p.created} better="down" />
        </Kpi>
        <Kpi
          label="Tickets solved"
          value={formatNumber(c.solved)}
          icon={CheckCircle2}
          tint="bg-emerald-50 text-emerald-600 ring-emerald-100"
          trend={{ values: o.volume.map((v) => v.solved), color: SERIES[1] }}
        >
          <Delta cur={c.solved} prev={p.solved} better="up" />
        </Kpi>
        <Kpi
          label="Avg first response"
          value={formatDuration(c.avgFirstResponseMinutes)}
          icon={Timer}
          tint="bg-brand-50 text-brand-600 ring-brand-100"
        >
          <Delta cur={c.avgFirstResponseMinutes} prev={p.avgFirstResponseMinutes} better="down" />
        </Kpi>
        <Kpi
          label="Avg resolution time"
          value={formatDuration(c.avgResolutionMinutes)}
          icon={Gauge}
          tint="bg-indigo-50 text-indigo-600 ring-indigo-100"
        >
          <Delta cur={c.avgResolutionMinutes} prev={p.avgResolutionMinutes} better="down" />
        </Kpi>
        <Kpi
          label="CSAT"
          value={pct(c.csatScore)}
          hint="Share of ratings that are 4 or 5 stars"
          icon={Smile}
          tint="bg-amber-50 text-amber-600 ring-amber-100"
        >
          <Delta cur={c.csatScore} prev={p.csatScore} better="up" unit="pts" />
          <p className="mt-0.5 text-[11px] text-slate-400">{c.csatResponses} ratings</p>
        </Kpi>
        <Kpi
          label="First-response SLA met"
          icon={ShieldCheck}
          tint="bg-teal-50 text-teal-600 ring-teal-100"
          value={pct(c.slaCompliance)}
          hint="Tickets answered within their priority's target"
        >
          <Delta cur={c.slaCompliance} prev={p.slaCompliance} better="up" unit="pts" />
          <p className="mt-0.5 text-[11px] text-slate-400">{c.slaBreached} breached</p>
        </Kpi>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-3">
        <Card className="p-5 xl:col-span-2">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold">Ticket volume</h2>
              <p className="text-xs text-slate-500">Created vs solved per day</p>
            </div>
            <div className="flex items-center gap-4">
              <Legend labels={['Created', 'Solved']} />
              <button
                type="button"
                onClick={() => setAsTable((v) => !v)}
                className="text-brand-600 text-xs font-medium hover:underline"
              >
                {asTable ? 'View chart' : 'View table'}
              </button>
            </div>
          </div>
          {asTable ? (
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white text-xs text-slate-500">
                  <tr>
                    <th scope="col" className="py-1.5 text-left font-medium">
                      Date
                    </th>
                    <th scope="col" className="py-1.5 text-right font-medium">
                      Created
                    </th>
                    <th scope="col" className="py-1.5 text-right font-medium">
                      Solved
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 tabular-nums">
                  {[...o.volume].reverse().map((v) => (
                    <tr key={v.date}>
                      <td className="py-1.5 text-slate-600">{v.date}</td>
                      <td className="py-1.5 text-right">{v.created}</td>
                      <td className="py-1.5 text-right">{v.solved}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <LineChart
              dates={o.volume.map((v) => v.date)}
              series={[
                { label: 'Created', values: o.volume.map((v) => v.created) },
                { label: 'Solved', values: o.volume.map((v) => v.solved) },
              ]}
            />
          )}
        </Card>

        <Card className="p-5">
          <h2 className="text-base font-bold">Backlog right now</h2>
          <p className="text-xs text-slate-500">Tickets still needing work</p>
          <dl className="mt-4 grid grid-cols-2 gap-3">
            {(
              [
                ['Open', o.backlog.open],
                ['Pending', o.backlog.pending],
                ['On hold', o.backlog.on_hold],
                ['Unassigned', o.backlog.unassigned],
              ] as const
            ).map(([label, n]) => (
              <div key={label} className="rounded-lg bg-slate-50 p-3">
                <dt className="text-xs text-slate-500">{label}</dt>
                <dd className="font-heading text-xl font-bold tabular-nums">{n}</dd>
              </div>
            ))}
            <div
              className={cn('col-span-2 rounded-lg p-3', o.backlog.overdue ? 'bg-red-50' : 'bg-emerald-50')}
            >
              <dt className={cn('text-xs', o.backlog.overdue ? 'text-red-700' : 'text-emerald-700')}>
                Past first-response SLA
              </dt>
              <dd
                className={cn(
                  'font-heading text-xl font-bold tabular-nums',
                  o.backlog.overdue ? 'text-red-800' : 'text-emerald-800',
                )}
              >
                {o.backlog.overdue}
                <span className="ml-2 text-xs font-normal">
                  {o.backlog.overdue ? 'need a reply now' : 'all on time'}
                </span>
              </dd>
            </div>
          </dl>
        </Card>
      </div>

      <div className="mt-5 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <Card className="p-5">
          <h2 className="mb-4 text-sm font-bold">By channel</h2>
          <BarList
            rows={o.byChannel.map((r) => ({
              label: r.key ? (CHANNEL_LABEL[r.key as TicketChannel] ?? r.key) : 'Unknown',
              value: r.count,
            }))}
          />
        </Card>
        <Card className="p-5">
          <h2 className="mb-4 text-sm font-bold">By topic</h2>
          <BarList
            rows={o.byCategory
              .slice(0, 8)
              .map((r) => ({ label: r.key ? humanize(r.key) : 'Uncategorised', value: r.count }))}
            total={c.created}
          />
        </Card>
        <Card className="p-5">
          <h2 className="mb-4 text-sm font-bold">By priority</h2>
          <BarList
            rows={['urgent', 'high', 'normal', 'low'].flatMap((k) => {
              const r = o.byPriority.find((x) => x.key === k);
              return r ? [{ label: humanize(k), value: r.count }] : [];
            })}
          />
        </Card>
        <Card className="p-5">
          <h2 className="mb-4 text-sm font-bold">By team</h2>
          <BarList rows={o.byTeam.map((r) => ({ label: teamName(r.key), value: r.count }))} />
        </Card>
      </div>

      <Card className="mt-5">
        <div className="border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-bold">Agent performance</h2>
          <p className="text-xs text-slate-500">
            Solved and response times in this period; open tickets right now
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px] text-left text-sm">
            <thead className="bg-slate-50/70 text-[11px] font-semibold tracking-wider text-slate-500 uppercase">
              <tr className="border-b border-slate-100">
                <th scope="col" className="px-5 py-3">
                  Agent
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Solved
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Open now
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Avg first response
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  CSAT
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 tabular-nums">
              {o.agents.map((a) => (
                <tr key={a.userId}>
                  <td className="px-5 py-3">
                    <span className="flex items-center gap-2.5 font-medium text-slate-800">
                      <Avatar name={a.name} size="sm" /> {a.name}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-900">{a.solved}</td>
                  <td className="px-5 py-3 text-right text-slate-700">{a.open}</td>
                  <td className="px-5 py-3 text-right text-slate-700">
                    {formatDuration(a.avgFirstResponseMinutes)}
                  </td>
                  <td className="px-5 py-3 text-right text-slate-700">
                    {pct(a.csatScore)}
                    {a.csatResponses > 0 && (
                      <span className="ml-1 text-xs text-slate-400">({a.csatResponses})</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
